import * as React from 'react';
import { ButtonPrimary, CloseButton, ButtonTertiary } from 'ui/common/button';
import { FixedDocument } from 'app/components/fixed-document';
import styles from './modal-wrapper.scss';
import { ButtonSecondary } from 'ui/common/button/button-secondary';
import { ModalSize } from 'app/components/constants';
import { keyCodeTypes } from 'ui/common/key-code-types';
import { ModalStack } from 'app/components/modal-wrapper/modal-stack';
import { ReactNode } from 'react';

let highestModalZindex: number = 300;

export interface ModalWrapperProps {
  headerTitle?: string | ReactNode;
  headerInfo?: string;
  btnTitle?: string;
  btnActionTitle?: string;
  btnCancelTitle?: string;
  onClose: () => void;
  onDone?: () => void;
  onAction?: () => void;
  isDisabled?: boolean | (() => boolean);
  isActionDisabled?: boolean | (() => boolean);
  isCancelDisabled?: boolean | (() => boolean);
  size?: typeof ModalSize[keyof typeof ModalSize];
  closeOnDone?: boolean;
  canCloseOnBackground?: boolean;
  canCloseOnXButton?: boolean;
  onExtraFooterAction?: () => void;
  btnExtraFooterActionTitle?: string;
  isExtraFooterActionDisabled?: boolean | (() => boolean);
  invertButtonOrder?: boolean;
  children?: React.ReactNode;
  btnExtraFooterActionOverride?: React.ReactNode;
  textClose?: string;
  showHeader?: boolean;
  hideCloseButton?: boolean;
}

interface ModalWrapperState {
  preventBackgroundClick: boolean;
}

const FocusWaitTypes = ['TEXTAREA', 'INPUT'];

export class ModalWrapper extends React.Component<ModalWrapperProps, ModalWrapperState> {
  private zindex: number = highestModalZindex;
  private titleRef: React.RefObject<HTMLDivElement> = React.createRef();
  public static defaultProps: Partial<ModalWrapperProps> = {
    closeOnDone: true,
    size: ModalSize.default,
    canCloseOnBackground: true,
    hideCloseButton: false
  };

  private handleDone = () => {
    this.props.closeOnDone && this.props.onClose();
    this.props.onDone?.();
  };

  private clickedScrollbar = (e: React.MouseEvent<HTMLDivElement>) => {
    const elementWithScrollbar = e.currentTarget;
    return !!elementWithScrollbar && (elementWithScrollbar.clientWidth <= e.clientX || elementWithScrollbar.clientHeight <= e.clientY);
  };

  private onClickBackground = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target !== e.currentTarget || FocusWaitTypes.indexOf(document.activeElement.nodeName) !== -1) {
      return;
    }

    if (this.state && this.state.preventBackgroundClick) {
      this.setState({ preventBackgroundClick: false });
      return;
    }

    if (!this.clickedScrollbar(e) && this.props.canCloseOnBackground) {
      this.props.onClose();
    }
  };

  private handleGlobalKeyDown = (e: KeyboardEvent) => {
    if (e.key === keyCodeTypes.Esc) {
      if (this.zindex === highestModalZindex) {
        this.props.onClose();
      }
    }
  };

  public componentDidMount() {
    highestModalZindex++;
    this.zindex = highestModalZindex;

    if (this.titleRef && this.titleRef.current) {
      this.titleRef.current.focus();
    }
    ModalStack.push(this);
    document.addEventListener('keydown', this.handleGlobalKeyDown);
  }

  public componentWillUnmount() {
    ModalStack.pop(this);
    document.removeEventListener('keydown', this.handleGlobalKeyDown);
  }

  public componentDidUpdate(prevProps: ModalWrapperProps) {
    if (prevProps.hideCloseButton && !this.props.hideCloseButton) {
      document.activeElement instanceof HTMLElement &&
        document.activeElement.blur();

      this.titleRef.current?.focus();
    }
  }

  private onBackgroundFocus = (e: React.FocusEvent<HTMLDivElement>) => {
    const relative = e.relatedTarget as HTMLElement;
    if (relative && e.target === e.currentTarget && FocusWaitTypes.indexOf(relative.nodeName) !== -1) {
      this.setState({ preventBackgroundClick: true });
    }
  };

  private isDisabled(): boolean {
    if (typeof this.props.isDisabled === 'function') {
      return this.props.isDisabled();
    }
    return !!this.props.isDisabled;
  }

  private isActionDisabled(): boolean {
    if (this.props.isActionDisabled === undefined) {
      return this.isDisabled();
    }

    if (typeof this.props.isActionDisabled === 'function') {
      return this.props.isActionDisabled();
    }
    return !!this.props.isActionDisabled;
  }

  private isExtraFooterActionDisabled(): boolean {
    if (this.props.isExtraFooterActionDisabled === undefined) {
      return this.isDisabled();
    }

    if (typeof this.props.isExtraFooterActionDisabled === 'function') {
      return this.props.isExtraFooterActionDisabled();
    }
    return !!this.props.isExtraFooterActionDisabled;
  }

  private isCancelDisabled(): boolean {
    if (typeof this.props.isCancelDisabled === 'function') {
      return this.props.isCancelDisabled();
    }
    return !!this.props.isCancelDisabled;
  }

  private onClose = () => {
    const { canCloseOnXButton = true, onClose } = this.props;
    canCloseOnXButton && onClose();
  };

  public render() {
    const { canCloseOnXButton = true, btnExtraFooterActionOverride, textClose, size, showHeader = true } = this.props;
    const showDoneButton = !!this.props.onDone;
    const showActionButton = !!this.props.onAction;
    const showCancelButton = !!this.props.btnCancelTitle;
    const showExtraFooterActionButton = (!!this.props.onExtraFooterAction && !!this.props.btnExtraFooterActionTitle);

    const styleOverride = { zIndex: this.zindex };

    const cancelButton = showCancelButton && <ButtonTertiary
      disabled={this.isCancelDisabled()}
      onClick={this.props.onClose}>
      {this.props.btnCancelTitle}
    </ButtonTertiary>;

    const doneButton = showDoneButton && <ButtonPrimary
      disabled={this.isDisabled()}
      onClick={this.handleDone}>
      {this.props.btnTitle}
    </ButtonPrimary>;

    return (
      <FixedDocument>
        <div
          className={`modal-background ${styles.modalBackground}`}
          style={styleOverride}
          onClick={this.onClickBackground}
          onFocus={this.onBackgroundFocus}
        >
          <div className={`modal modal-dialog modal-${size} ${styles.modalDialog}`}
            data-tabindex="-1" role="dialog" data-inert="true">
            <div className="modal-content" role="document">
              {showHeader && <div className="modal-header" tabIndex={-1} ref={this.titleRef}>
                <h5 className="modal-title">
                  {this.props.headerTitle}
                </h5>
                {!this.props.hideCloseButton && 
                  <CloseButton onClick={this.onClose} tabIndex={-1} disabled={!canCloseOnXButton} textClose={textClose} />}
              </div>}
              {this.props.headerInfo && <p className={styles.modalHeaderInfo}>{this.props.headerInfo}</p>}
              <div className="modal-body">
                {this.props.children}
              </div>
              {
                (showDoneButton || showCancelButton || showActionButton
                  || showExtraFooterActionButton || btnExtraFooterActionOverride)
                && <div className="modal-footer">
                  {btnExtraFooterActionOverride
                    ? <span>{btnExtraFooterActionOverride}</span>
                    : showExtraFooterActionButton
                      ? <ButtonTertiary
                        disabled={this.isExtraFooterActionDisabled()}
                        onClick={this.props.onExtraFooterAction}>
                        {this.props.btnExtraFooterActionTitle}
                      </ButtonTertiary>
                      : ''}
                  {!!this.props.invertButtonOrder ? doneButton : cancelButton}
                  {showActionButton && <ButtonSecondary
                    disabled={this.isActionDisabled()}
                    onClick={this.props.onAction}>
                    {this.props.btnActionTitle}
                  </ButtonSecondary>}
                  {!!this.props.invertButtonOrder ? cancelButton : doneButton}
                </div>
              }
            </div>
          </div>
        </div>
      </FixedDocument>
    );
  }
}
